import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { peekProxy } from '#~/agent/utils.js';
import { useDepot } from '#~/depot/hooks.js';
import { type Track } from '#~/livekit/index.js';
import { broker } from './broker.js';
import { locateLiveKit } from './utils.js';
import type { Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { DeviceGovernorState } from './bandwidth-governor.js';
import type { LiveKit } from './types.js';
import { useTrialCameraLocked } from '#~/api/billing/camera-access.js';
import {
  getVideoTrackWaitState,
  subscribeVideoTrackWait,
  type VideoTrackWaitState
} from './track-wait.js';

/** Subscribe to a room's broker-owned LiveKit resource across creation and replacement. */
export const useRoomLiveKit = (roomId: Room['id'] | undefined): T_Maybe<LiveKit> => {
  const getSnapshot = useCallback(() => broker.acquire(roomId), [roomId]);

  return useSyncExternalStore(broker.subscribe, getSnapshot, getSnapshot);
};

/*
 * LiveKit tracks are stored outside the depot store. The track hooks below resolve the owning
 * proxy and its track inside a `useDepot` selector, so every depot write (state sync writes on
 * every accepted device transaction) re-checks whether the device has landed under its proxy.
 * The selector returns the stable `Track` or `undefined`, so unrelated writes do not re-render.
 */

/**
 * Returns the LiveKit audio track for the specified device.
 *
 * @param deviceId - Audio device identifier.
 * @returns LikeKit audio track or `undefined`.
 */
export const useAudioTrack = (deviceId: Device['id'], roomId?: Room['id']): T_Maybe<Track> => {
  const roomLiveKit = useRoomLiveKit(roomId);

  // ENG-4898: resolve the owning proxy inside the selector. A restored source can render
  // before its Bridge proxy owns the device; a render-time lookup would keep that miss.
  // `peekProxy` is read-only, so the selector has no side effects.
  return useDepot(() => {
    const proxy = peekProxy(deviceId);
    return proxy
      ? (roomLiveKit ?? locateLiveKit(deviceId))
          ?.audioForAgent(proxy.id, proxy.embedded)
          .get(deviceId)
      : undefined;
  });
};

/**
 * Returns the LiveKit video track for the specified device.
 *
 * @param deviceId - Video device identifier.
 * @returns LikeKit video track or `undefined`.
 */
export const useVideoTrack = (deviceId: Device['id'], roomId?: Room['id']): T_Maybe<Track> => {
  // Keep restored-source discovery reactive while also subscribing to trial locks.
  const proxy = useDepot(() => peekProxy(deviceId));
  const locked = useTrialCameraLocked(proxy?.roomId ?? roomId, proxy?.id, deviceId);
  const roomLiveKit = useRoomLiveKit(roomId);

  // Recheck late Bridge transactions without requiring the operator to reselect the camera.
  return useDepot(() => {
    const currentProxy = peekProxy(deviceId);
    return currentProxy && !locked
      ? (roomLiveKit ?? locateLiveKit(deviceId))
          ?.videoForAgent(currentProxy.id, currentProxy.embedded)
          .get(deviceId)
      : undefined;
  });
};

/**
 * ENG-3716: subscribe to the waiting-for-video-track state machine for a
 * device. Re-renders when the device's connect wait starts, resolves, times
 * out, or fails — so the badge can leave "Connecting..." for a concrete,
 * cause-keyed failure state instead of waiting forever.
 */
export const useVideoTrackWaitState = (deviceId: Device['id']): T_Maybe<VideoTrackWaitState> => {
  const getSnapshot = useCallback(() => getVideoTrackWaitState(deviceId), [deviceId]);

  return useSyncExternalStore(subscribeVideoTrackWait, getSnapshot, getSnapshot);
};

/**
 * Subscribe to the bandwidth governor state for a specific device.
 * Returns the current DeviceGovernorState ('normal', 'quality-capped',
 * 'paused-auto', 'paused-manual') and re-renders when it changes.
 * Per rule: think-in-systems -- reactive hook driven by existing event emitter.
 */
export const useBandwidthDeviceState = (
  deviceId: Device['id'],
  roomId?: Room['id']
): DeviceGovernorState => {
  const livekit = useRoomLiveKit(roomId) ?? locateLiveKit(deviceId);
  const [state, setState] = useState<DeviceGovernorState>(
    () => livekit?.getBandwidthDeviceState(deviceId) ?? 'normal'
  );

  useEffect(() => {
    if (!livekit) return;

    // Sync initial state in case it changed between render and effect
    setState(livekit.getBandwidthDeviceState(deviceId));

    const handler = (event: { deviceId: Device['id']; state: DeviceGovernorState }) => {
      if (event.deviceId === deviceId) {
        setState(event.state);
      }
    };

    livekit.on('bandwidthDeviceStateChanged', handler);
    return () => {
      livekit.off('bandwidthDeviceStateChanged', handler);
    };
  }, [livekit, deviceId]);

  return state;
};
